import * as React from 'react';

/**
 * GIS coordinate formatters (degrees-minutes-seconds).
 *
 * These are local replacements for the `Latitude`/`Longitude` formatters in
 * `@independent-software/typeui/formatters/GIS`, which pick the hemisphere from
 * the (always non-negative) degrees component and therefore render every
 * longitude as "E" and every latitude as "N". Here the hemisphere is derived
 * from the sign of the original value, so negative values correctly show
 * "W"/"S".
 */

interface IProps {
  /** Value to format. */
  value: number | string;
  /** String to show if formatting failed. */
  default?: string;
}

function toDMS(value: number | string, defaultStr: string | undefined, max: number, positive: string, negative: string): string {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num) || num < -max || num > max) return defaultStr ? defaultStr : "(no coordinates)";

  const abs = Math.abs(num);
  const degrees = Math.trunc(abs);
  let seconds = (abs - degrees) * 3600;
  // Seconds are multiplied by 10,000, rounded, then divided again to avoid float errors.
  const minutes = Math.trunc(Math.round((seconds / 60) * 10000) / 10000);
  seconds = Math.round(seconds) % 60;

  const ms = minutes.toString().padStart(2, '0');
  const ss = seconds.toFixed(0).padStart(2, '0');
  return `${degrees}° ${ms}′ ${ss}″ ${num >= 0 ? positive : negative}`;
}

class Latitude extends React.Component<IProps> {
  render(): string {
    return toDMS(this.props.value, this.props.default, 90, "N", "S");
  }
}

class Longitude extends React.Component<IProps> {
  render(): string {
    return toDMS(this.props.value, this.props.default, 180, "E", "W");
  }
}

export { Latitude, Longitude };
